import { useState } from "react";
import { Trash, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ServicePicker, type SelectedLineItem } from "./ServicePicker";
import { useCreateInvoice } from "@/lib/mutations";
import { usePatients } from "@/lib/queries";
import { fmtINR, fmtINRInput } from "@emr/shared/india";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  patientId?: string;
  visitId?: string;
  onCreated?: (invoiceId: string) => void;
}

export function InvoiceForm({ open, onOpenChange, patientId: _patientId, visitId, onCreated }: Props) {
  const [patientSearch, setPatientSearch] = useState("");
  const [patientId, setPatientId] = useState(_patientId ?? "");
  const [lineItems, setLineItems] = useState<SelectedLineItem[]>([]);
  const createMut = useCreateInvoice();
  const { data: patientsData } = usePatients(patientSearch.length >= 2 ? patientSearch : "", 1, 20);
  const patients = patientsData?.rows ?? [];

  const subtotal = lineItems.reduce((sum, i) => sum + i.amountPaise * i.quantity, 0);
  const total = subtotal;

  const addLine = (item: SelectedLineItem) => {
    setLineItems((prev) => [...prev, item]);
  };

  const updateQty = (idx: number, qty: number) => {
    setLineItems((prev) => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, qty) } : it));
  };

  const updateAmount = (idx: number, rupees: string) => {
    const paise = Math.round((parseFloat(rupees) || 0) * 100);
    setLineItems((prev) => prev.map((it, i) => i === idx ? { ...it, amountPaise: paise } : it));
  };

  const removeLine = (idx: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!patientId) {
      toast.error("Select a patient");
      return;
    }
    if (lineItems.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    try {
      const res = await createMut.mutateAsync({
        patientId,
        visitId: visitId ?? null,
        lineItems: lineItems.map((i) => ({ serviceId: i.serviceId, name: i.name, quantity: i.quantity, amountPaise: i.amountPaise })),
        subtotalPaise: subtotal,
        totalPaise: total,
      });
      onOpenChange(false);
      setLineItems([]);
      setPatientSearch("");
      setPatientId("");
      onCreated?.(res.invoice.id);
    } catch {
      // handled
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!_patientId && (
            <div>
              <Label>Patient</Label>
              <Input
                placeholder="Search patient by name/phone/UHID…"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
              />
              {patients.length > 0 && (
                <div className="mt-1 max-h-32 overflow-y-auto rounded-md border border-cream-200 bg-cream-100 dark:border-dark-700">
                  {patients.slice(0, 10).map((p) => (
                    <button
                      key={p.id}
                      className="flex w-full justify-between border-b border-cream-200 px-3 py-1 text-left text-sm hover:bg-cream-200 dark:border-dark-700 dark:hover:bg-dark-700"
                      onClick={() => { setPatientId(p.id); setPatientSearch(`${[p.salutation, p.name].filter(Boolean).join(" ")} · ${p.clinicUhid}`); }}
                    >
                      <span className="text-ink dark:text-cream-50">{[p.salutation, p.name].filter(Boolean).join(" ")}</span>
                      <span className="text-xs text-muted">{p.clinicUhid}</span>
                    </button>
                  ))}
                </div>
              )}
              {patientId && <div className="mt-1 text-xs text-green-700">Patient selected.</div>}
            </div>
          )}

          <div>
            <Label>Line items</Label>
            <ServicePicker onAdd={addLine} />
          </div>

          {lineItems.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="bg-cream-100 dark:bg-dark-700">
                  <TableHead>Service</TableHead>
                  <TableHead className="w-20">Qty</TableHead>
                  <TableHead className="w-32">Amount (₹)</TableHead>
                  <TableHead className="w-24">Total</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-sm">{it.name}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        value={it.quantity}
                        onChange={(e) => updateQty(idx, parseInt(e.target.value, 10) || 1)}
                        className="h-8 w-16"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        value={fmtINRInput(it.amountPaise)}
                        onChange={(e) => updateAmount(idx, e.target.value)}
                        className="h-8 w-28"
                      />
                    </TableCell>
                    <TableCell className="text-sm">{fmtINR(it.amountPaise * it.quantity)}</TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeLine(idx)}>
                        <Trash size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="flex justify-end gap-4">
            <div className="text-right text-sm">
              <div><span className="text-muted">Subtotal: </span>{fmtINR(subtotal)}</div>
              <div className="text-base font-bold text-maroon-700 dark:text-amber-500"><span className="text-muted">Total: </span>{fmtINR(total)}</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={createMut.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Plus size={14} className="mr-1" /> Create Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
